import { cn } from '@noor/lib/cn';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import * as React from 'react';

function Table({
  className,
  containerClassName,
  containerRef,
  ...props
}: React.ComponentProps<'table'> & {
  containerClassName?: string;
  containerRef?: React.Ref<HTMLDivElement> | undefined;
}) {
  return (
    <div
      ref={containerRef}
      data-slot="table-container"
      data-scroll-to-top
      className={cn('relative w-full overflow-auto', containerClassName)}
    >
      <table
        data-slot="table"
        className={cn('w-full caption-bottom text-sm', className)}
        {...props}
      />
    </div>
  );
}

function TableHeader({ className, ...props }: React.ComponentProps<'thead'>) {
  return (
    <thead
      data-slot="table-header"
      // Sticky within the scroll container. The bottom divider lives as an inset
      // box-shadow on the head cells (below) because a collapsed-border on a
      // sticky thead detaches while scrolling. The fill is SOLID (bg-card) —
      // a translucent header lets scrolled rows bleed through. Figma
      // 987:9268 draws the header on the same white surface as the rows.
      className={cn('sticky top-0 z-10 bg-card', className)}
      {...props}
    />
  );
}

function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
  return (
    <tbody
      data-slot="table-body"
      className={cn('[&_tr:last-child]:border-0', className)}
      {...props}
    />
  );
}

function TableFooter({ className, ...props }: React.ComponentProps<'tfoot'>) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn(
        'border-t border-table-border bg-table-header-bg font-medium [&>tr]:last:border-b-0',
        className,
      )}
      {...props}
    />
  );
}

function TableRow({ className, ...props }: React.ComponentProps<'tr'>) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        // Inside <thead> the row drops its own bottom border AND hover tint:
        // the head cells already paint the divider as an inset shadow (see
        // TableHeader), so a row border here would double the line sitting on
        // top of the first body row.
        'border-b border-table-border transition-colors hover:bg-table-row-hover data-[state=selected]:bg-table-row-hover [thead_&]:border-b-0 [thead_&]:hover:bg-transparent',
        className,
      )}
      {...props}
    />
  );
}

function TableHead({ className, ...props }: React.ComponentProps<'th'>) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        // Figma header cell (987:9268): 40px row, 13px medium grey labels,
        // 16px gutters, vertically centered (12px top/bottom on a 16px line).
        // Sortable headers trailing the stacked TableSortIcon keep the label
        // left and the chevrons right with justify-between in the caller.
        // The bottom divider is an inset shadow (see TableHeader).
        'h-10 px-4 py-2 text-left align-middle text-[13px] leading-4 font-medium whitespace-nowrap text-table-header-fg shadow-[inset_0_-1px_0_0_var(--table-border)] [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
        className,
      )}
      {...props}
    />
  );
}

function TableCell({ className, ...props }: React.ComponentProps<'td'>) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        // Figma body cell (987:9268): 40px row, 14px regular black on a 20px
        // line, 16px gutters. Content centers vertically via align-middle
        // (10px top/bottom on a 20px line).
        'h-10 px-4 py-2 align-middle text-sm leading-5 whitespace-nowrap text-table-cell-fg [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
        className,
      )}
      {...props}
    />
  );
}

function TableCaption({ className, ...props }: React.ComponentProps<'caption'>) {
  return (
    <caption
      data-slot="table-caption"
      className={cn('mt-4 text-sm text-muted-foreground', className)}
      {...props}
    />
  );
}

/** Active sort direction for a sortable column header (`null` = unsorted). */
export type TableSortDirection = 'asc' | 'desc' | null;

/**
 * The stacked up/down chevrons trailing a sortable column header (exact
 * Figma): two 14px glyphs, the active direction in slate, the idle one pale.
 * Presentational — the header button owns the click + aria-sort.
 */
function TableSortIcon({
  direction = null,
  className,
}: {
  direction?: TableSortDirection;
  className?: string;
}) {
  return (
    <span
      data-slot="table-sort-icon"
      aria-hidden="true"
      className={cn('inline-flex shrink-0 flex-col items-center', className)}
    >
      <ChevronUpIcon
        className={cn(
          '-mb-1 size-[14px]',
          direction === 'asc' ? 'text-table-sort-fg-active' : 'text-table-sort-fg-idle',
        )}
      />
      <ChevronDownIcon
        className={cn(
          '-mt-1 size-[14px]',
          direction === 'desc' ? 'text-table-sort-fg-active' : 'text-table-sort-fg-idle',
        )}
      />
    </span>
  );
}

export {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  TableSortIcon,
};
